/**
 * AJAX Cart Drawer
 * Handles all cart drawer functionality including open/close, add to cart, update quantity, and remove items
 */

class AjaxCartDrawer {
  constructor() {
    this.drawer = document.getElementById("ajax-cart-drawer");
    if (!this.drawer) return;

    this.openCartBtn = document.querySelectorAll("[data-open-cart]");
    this.overlay = this.drawer.querySelector(".ajax-cart-drawer__overlay");
    this.closeBtn = this.drawer.querySelector(".ajax-cart-drawer__close");
    this.continueBtn = this.drawer.querySelector(".ajax-cart-drawer__continue");
    this.itemsContainer = this.drawer.querySelector(".ajax-cart-drawer__items");
    this.emptyState = this.drawer.querySelector(".ajax-cart-drawer__empty");
    this.subtotalPrice = this.drawer.querySelector(".ajax-cart-drawer__subtotal-price");
    this.body = this.drawer.querySelector(".ajax-cart-drawer__body");
    this.recsContainer = this.drawer.querySelector(".ajax-cart-drawer__recs");
    this.cartItems = [];
    this.recs = [];
    this.defaultRecs = this.drawer.querySelector("[data-cart-default-related-products]");
    this.swiper = null;
    
    // AJAX queue for recommendations
    this.ajaxQueue = [];
    this.isProcessingQueue = false;
    
    // Queue system for slider operations
    this.sliderQueue = [];
    this.isProcessingSlider = false;

    this.init();
  }

  init() {
    // Close drawer events
    this.closeBtn?.addEventListener("click", () => this.close());
    this.overlay?.addEventListener("click", () => this.close());
    this.continueBtn?.addEventListener("click", () => this.close());

    // Close on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.drawer.classList.contains("is-open")) {
        this.close();
      }
    });

    // Prevent body scroll when drawer is open
    this.drawer.addEventListener("transitionend", (e) => {
      if (e.target === this.drawer && this.drawer.classList.contains("is-open")) {
        document.body.style.overflow = "hidden";
      }
    });

    this.openCartBtn.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }

  update;

  open() {
    this.drawer.classList.add("is-open");
    document.body.style.overflow = "hidden";
    this.fetchCart();
  }

  close() {
    this.drawer.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  async fetchCart() {
    try {
      this.body.classList.add("loading");
      const response = await fetch("/cart.js");
      const cart = await response.json();
      this.renderCart(cart);
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      this.body.classList.remove("loading");
    }
  }

  updateCartCount(count) {
    console.log("updateCartCount", count);
    const cartCountElements = document.querySelectorAll("[data-cart-count]");
    cartCountElements.forEach((el) => {
      el.textContent = count;
    });
  }

  async renderCart(cart) {
    // Update cart count in header if exists
    this.updateCartCount(cart.item_count);

    // Show empty state or items
    if (cart.items.length === 0) {
      this.emptyState.style.display = "flex";
      this.itemsContainer.style.display = "none";
      this.drawer.querySelector(".ajax-cart-drawer__footer").style.display = "none";
      this.renderRecs([]);
    } else {
      this.emptyState.style.display = "none";
      this.itemsContainer.style.display = "flex";
      this.drawer.querySelector(".ajax-cart-drawer__footer").style.display = "flex";
      this.renderItems(cart.items);
      this.updateSubtotal(cart.total_price);
      this.renderRecs(cart.items);
    }

    this.checkRecsProducts(cart.items);

    // Queue slider operations to prevent conflicts
    this.queueSliderOperation(cart.items);
  }

  queueSliderOperation(items) {
    // Add operation to queue
    this.sliderQueue.push({ items });
    
    // Process queue if not already processing
    if (!this.isProcessingSlider) {
      this.processSliderQueue();
    }
  }

  async processSliderQueue() {
    if (this.sliderQueue.length === 0) {
      this.isProcessingSlider = false;
      return;
    }

    this.isProcessingSlider = true;
    
    // Get the next operation from the queue
    const operation = this.sliderQueue.shift();
    
    try {

      await new Promise(resolve => setTimeout(resolve, 0));
      
    } catch (error) {
      console.error('Error processing slider queue:', error);
    } finally {
      // Process next item in queue
      this.processSliderQueue();
    }
  }

  async renderRecs(items) {
    // Clear the queue
    this.recs = [];
    this.recsContainer.querySelector('.swiper-wrapper').innerHTML = '';
    this.ajaxQueue = [];
    
    // Add all fetch requests to the queue
    items.forEach((item) => {
      this.ajaxQueue.push({
        url: `/products/${item.handle}?view=recs`,
        item: item
      });
    });
    
    // Process the queue and collect results
    await this.processRecsQueue(items);
  }
  
  async processRecsQueue(items) {
    this.isProcessingQueue = true;
    
    try {
      // Process all requests in the queue
      const promises = this.ajaxQueue.map(queueItem => 
        this.fetchRecsForItem(queueItem.url)
          .catch(error => {
            console.error('Error fetching related products recs:', error);
            return null; // Return null for failed requests
          })
      );
      
      // Wait for all requests to complete
      const results = await Promise.all(promises);
      
      // Collect all product items from successful results
      results.forEach(productItems => {
        if (productItems && productItems.length > 0) {
          productItems.forEach(p => this.recs.push(p));
        }
      });
      
      console.log("recs", this.recs);

      this.fillRecs(items);
      
    } catch (error) {
      console.error('Error processing recs queue:', error);
    } finally {
      this.isProcessingQueue = false;
      this.ajaxQueue = [];
    }
  }

  fillRecs(items) {
    // Make a copy of the default recommendation product items so we're not moving them from the DOM
    const defaultRecsNodes = Array.from(this.defaultRecs.querySelectorAll(".product-item")).map(node => node.cloneNode(true));
    const finalRecs = [...this.recs, ...defaultRecsNodes];

    const itemProductIds = new Set(items.map((item) => String(item.product_id)));
    // Remove items from finalRecs whose product ID is in itemProductIds
    // Assuming product-id is stored as data-product-id attribute on .product-item elements
    const filteredRecs = finalRecs.filter(item => {
      const productId = item.getAttribute ? item.getAttribute('data-product-id') : null;
      return !itemProductIds.has(String(productId));
    });

    // Ensure only unique items by product-id
    const uniqueRecsMap = new Map();
    filteredRecs.forEach(item => {
      const productId = item.getAttribute ? item.getAttribute('data-product-id') : null;
      if (productId && !uniqueRecsMap.has(productId)) {
        uniqueRecsMap.set(productId, item);
      }
    });
    const uniqueFilteredRecs = Array.from(uniqueRecsMap.values());

    // Wrap each item from filteredRecs with '.swiper-slide' and insert into [data-ajax-cart-drawer-recs]
    const recsContainer = document.querySelector('[data-ajax-cart-drawer-recs] .swiper-wrapper');
    if (recsContainer) {
      recsContainer.innerHTML = ''; // Clear previous recs
      uniqueFilteredRecs.forEach(item => {
        const slide = document.createElement('div');
        slide.classList.add('swiper-slide');
        if (item instanceof HTMLElement) {
          slide.appendChild(item);
        } else if (item instanceof Node) {
          slide.appendChild(item.cloneNode(true));
        }
        recsContainer.appendChild(slide);
      });

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;       
      }

      this.renderSlider();
    }
  }
  
  async fetchRecsForItem(url) {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.status}`);
    }
    
    const htmlString = await response.text();
    
    // Parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    
    // Find related products nodes
    const relatedProductsContainer = doc.querySelector('[data-related-products]');
    
    console.log("relatedProductsContainer", doc);
    
    if (relatedProductsContainer) {
      // Find product-item elements (match the Liquid snippet)
      const productItems = relatedProductsContainer.querySelectorAll('.product-item');
      return Array.from(productItems);
    }
    
    return [];
  }

  renderSlider(items) {
    this.swiper = new Swiper(this.recsContainer, {
      slidesPerView: 2,
      spaceBetween: 12,
    });
  }

  renderItems(items) {
    this.itemsContainer.innerHTML = items.map((item) => this.createItemHTML(item)).join("");
    this.attachItemEventListeners();
  }

  createItemHTML(item) {
    const variantOptions = item.options_with_values
      .filter((option) => option.value !== "Default Title")
      .map((option) => `${option.name}: ${option.value}`)
      .join(" / ");

    return `
      <div class="ajax-cart-item" data-key="${item.key}" data-cart-item-title="${item.product_title}">
        <a href="${item.url}" class="ajax-cart-item__image">
          <img src="${item.image}" alt="${item.product_title}" loading="lazy">
        </a>
        
        <div class="ajax-cart-item__details">
          <a href="${item.url}" class="ajax-cart-item__title">${item.product_title}</a>
          ${variantOptions ? `<p class="ajax-cart-item__variant">${variantOptions}</p>` : ""}
          <p class="ajax-cart-item__price">${this.formatMoney(item.price)}</p>
          
          <div class="ajax-cart-item__quantity-wrap flex justify-between align-center">
            <div class="ajax-cart-item__quantity">
              <button class="ajax-cart-item__quantity-btn ajax-cart-item__quantity-decrease" 
                      data-key="${item.key}" 
                      aria-label="Decrease quantity">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
              <span class="ajax-cart-item__quantity-value">${item.quantity}</span>
              <button class="ajax-cart-item__quantity-btn ajax-cart-item__quantity-increase" 
                      data-key="${item.key}" 
                      aria-label="Increase quantity">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            </div>
            <p class="ajax-cart-item__total">${this.formatMoney(item.line_price)}</p>
          </div>
        </div>
        
        <div class="ajax-cart-item__remove">
          <button class="ajax-cart-item__remove-btn" 
                  data-key="${item.key}" 
                  aria-label="Remove item">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  attachItemEventListeners() {
    // Increase quantity
    this.itemsContainer.querySelectorAll(".ajax-cart-item__quantity-increase").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const key = e.currentTarget.dataset.key;
        const item = this.itemsContainer.querySelector(`[data-key="${key}"]`);
        const currentQty = parseInt(item.querySelector(".ajax-cart-item__quantity-value").textContent);
        this.updateQuantity(key, currentQty + 1);
      });
    });

    // Decrease quantity
    this.itemsContainer.querySelectorAll(".ajax-cart-item__quantity-decrease").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const key = e.currentTarget.dataset.key;
        const item = this.itemsContainer.querySelector(`[data-key="${key}"]`);
        const currentQty = parseInt(item.querySelector(".ajax-cart-item__quantity-value").textContent);
        if (currentQty > 1) {
          this.updateQuantity(key, currentQty - 1);
        }
      });
    });

    // Remove item
    this.itemsContainer.querySelectorAll(".ajax-cart-item__remove-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const key = e.currentTarget.dataset.key;
        this.removeItem(key);
      });
    });
  }

  async updateQuantity(key, quantity) {
    try {
      this.body.classList.add("loading");
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
        }),
      });

      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error("Error updating quantity:", error);
    } finally {
      this.body.classList.remove("loading");
    }
  }

  async removeItem(key) {
    const item = this.itemsContainer.querySelector(`[data-key="${key}"]`);
    if (item) {
      item.classList.add("removing");
    }

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        }),
      });

      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error("Error removing item:", error);
      if (item) {
        item.classList.remove("removing");
      }
    }
  }

  updateSubtotal(price) {
    if (this.subtotalPrice) {
      this.subtotalPrice.textContent = this.formatMoney(price);
    }
  }

  updateCartCount(count) {
    const cartCountElements = document.querySelectorAll(".cart-count, [data-cart-count]");
    cartCountElements.forEach((el) => {
      el.textContent = count;
    });
  }

  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  // Public method to add item to cart
  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.open();
      return data;
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  }
}

// Initialize cart drawer when DOM is ready (skip for B2B customers)
document.addEventListener("DOMContentLoaded", () => {
  if (window.isB2B) return;
  window.ajaxCartDrawer = new AjaxCartDrawer();
});
